import { useEffect } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import CustomForm from "../../../general/form/CustomForm";
import { isDefined } from "../../../../auxiliar/formatValidators";
import { postAbsence } from "../../../../actions/absence";

const RegisterAbsence = ({
    register,
    errors,
    isSubmitting,
    watch,
    setValue,
    handleSubmit,
    closeTooltip,
    eventData,
}) => {
    const activeClinic = useSelector((state) => state.activeClinic);

    const description = watch("description") || "";
    const startDatetime = watch("start_datetime") || "";
    const finishDatetime = watch("finish_datetime") || "";
    const firstDate = watch("first_date") || "";
    const startHour = watch("start_hour") || "";
    const finishHour = watch("finish_hour") || "";
    const recurrent = watch("recurrent") || false;
    const repeatSome =
        watch("repeat_monday") ||
        watch("repeat_tuesday") ||
        watch("repeat_wednesday") ||
        watch("repeat_thursday") ||
        watch("repeat_friday") ||
        watch("repeat_saturday") ||
        watch("repeat_sunday") ||
        false;

    // Initially sets the start and finish date and hour based on the event data
    useEffect(() => {
        setValue("clinic_id", activeClinic.id);
        setValue("employee_id", "all");
        if (eventData.startAt)
            setValue(
                "start_datetime",
                format(new Date(eventData.startAt), "yyyy-MM-dd'T'HH:mm").toString(),
            );
        if (eventData.endAt)
            setValue(
                "finish_datetime",
                format(new Date(eventData.endAt), "yyyy-MM-dd'T'HH:mm").toString(),
            );
    }, [eventData]);

    // Sets the start and finish date when recurrent changes
    useEffect(() => {
        if (recurrent) {
            if (isDefined(startDatetime)) {
                setValue("first_date", format(new Date(startDatetime), "yyyy-MM-dd").toString());
                setValue("start_hour", format(new Date(startDatetime), "HH:mm").toString());
            }
            if (isDefined(finishDatetime)) {
                setValue("finish_hour", format(new Date(finishDatetime), "HH:mm").toString());
            }
        } else {
            if (isDefined(firstDate)) {
                const auxDateStart = new Date(firstDate);
                if (isDefined(startHour)) {
                    auxDateStart.setHours(
                        parseInt(startHour.slice(0, 2)),
                        parseInt(startHour.slice(3, 5)),
                    );
                }
                setValue("start_datetime", format(auxDateStart, "yyyy-MM-dd'T'HH:mm").toString());

                const auxDateFinish = new Date(firstDate);
                if (isDefined(finishHour)) {
                    auxDateFinish.setHours(
                        parseInt(finishHour.slice(0, 2)),
                        parseInt(finishHour.slice(3, 5)),
                    );
                }
                setValue("finish_datetime", format(auxDateFinish, "yyyy-MM-dd'T'HH:mm").toString());
            }
        }
    }, [recurrent]);

    const formConfig = {
        employee_id: {
            condition: true,
            disabled: false,
            allowAll: true,
            defaultValue: "all",
            type: "physio_selector",
            label: "Fisioterapeuta *",
            name: "employee_id",
            filter: {
                active: true,
            },
        },
        name: {
            condition: true,
            disabled: false,
            label: "Título *",
            placeholder: "Título de la ausencia",
            name: "name",
            type: "text",
            validators: {
                required: "Escribir un título es obligatorio",
            },
        },
        start_datetime: {
            condition: !recurrent,
            disabled: false,
            type: "datetime-local",
            label: "Fecha y hora de inicio *",
            name: "start_datetime",
            validators: {
                required: "Elegir una fecha y hora de inicio es obligatorio",
                validate: (v) =>
                    !recurrent ||
                    isDefined(v) ||
                    "Elegir una fecha y hora de inicio es obligatorio",
            },
        },
        finish_datetime: {
            condition: !recurrent,
            disabled: false,
            type: "datetime-local",
            label: "Fecha y hora de fin *",
            name: "finish_datetime",
            validators: {
                required: "Elegir una fecha y hora de fin es obligatorio",
                validate: (v) => {
                    if (recurrent) return true;
                    if (!isDefined(v)) return "Elegir una fecha y hora de fin es obligatorio";
                    if (new Date(v).getTime() <= new Date(startDatetime).getTime())
                        return "La fecha de fin debe ser posterior a la de inicio";
                },
            },
        },
        first_date: {
            condition: recurrent,
            disabled: false,
            type: "date",
            label: "Fecha de inicio *",
            name: "first_date",
            validators: {
                required: "Elegir una fecha de inicio es obligatorio",
                validate: (v) =>
                    recurrent || isDefined(v) || "Elegir una fecha de inicio es obligatorio",
            },
        },
        last_date: {
            condition: recurrent,
            disabled: false,
            type: "date",
            label: "Fecha de fin *",
            name: "last_date",
            validators: {
                required: "Elegir una fecha de fin es obligatorio",
                validate: (v) => {
                    if (!recurrent) return true;
                    if (!isDefined(v)) return "Elegir una fecha de fin es obligatorio";
                    if (new Date(v).getTime() <= new Date(firstDate).getTime())
                        return "La fecha de fin debe ser posterior a la de inicio";
                },
            },
        },
        start_hour: {
            condition: recurrent,
            disabled: false,
            type: "time",
            label: "Hora de inicio *",
            name: "start_hour",
            validators: {
                required: "Elegir una hora de inicio es obligatorio",
                validate: (v) =>
                    recurrent || isDefined(v) || "Elegir una hora de inicio es obligatorio",
            },
        },
        finish_hour: {
            condition: recurrent,
            disabled: false,
            type: "time",
            label: "Hora de fin *",
            name: "finish_hour",
            validators: {
                required: "Elegir una hora de fin es obligatorio",
                validate: (v) => {
                    if (!recurrent) return true;
                    if (!isDefined(v)) return "Elegir una hora de fin es obligatorio";
                    if (v <= startHour) return "La hora de fin debe ser posterior a la de inicio";
                },
            },
        },
        description: {
            condition: true,
            disabled: false,
            type: "textarea",
            label: `Descripción (${description?.length || 0}/10000)`,
            placeholder: "...",
            name: "description",
            validators: {
                maxLength: {
                    value: 10000,
                    message: "La descripción es demasiado largo",
                },
            },
        },
        recurrent: {
            condition: true,
            disabled: false,
            label: "Recurrente",
            type: "checkbox",
            name: "recurrent",
        },
        repeat_monday: {
            condition: recurrent,
            disabled: false,
            info: "Día(s) en el que se repetirá la sesión",
            type: "checkbox",
            label: "Lunes",
            name: "repeat_monday",
            validators: {
                validate: () =>
                    !recurrent ||
                    repeatSome ||
                    "Elegir al menos un día de la semana es obligatorio",
            },
        },
        repeat_tuesday: {
            condition: recurrent,
            disabled: false,
            info: "Día(s) en el que se repetirá la sesión",
            type: "checkbox",
            label: "Martes",
            name: "repeat_tuesday",
            validators: {
                validate: () =>
                    !recurrent ||
                    repeatSome ||
                    "Elegir al menos un día de la semana es obligatorio",
            },
        },
        repeat_wednesday: {
            condition: recurrent,
            disabled: false,
            info: "Día(s) en el que se repetirá la sesión",
            type: "checkbox",
            label: "Miércoles",
            name: "repeat_wednesday",
            validators: {
                validate: () =>
                    !recurrent ||
                    repeatSome ||
                    "Elegir al menos un día de la semana es obligatorio",
            },
        },
        repeat_thursday: {
            condition: recurrent,
            disabled: false,
            info: "Día(s) en el que se repetirá la sesión",
            type: "checkbox",
            label: "Jueves",
            name: "repeat_thursday",
            validators: {
                validate: () =>
                    !recurrent ||
                    repeatSome ||
                    "Elegir al menos un día de la semana es obligatorio",
            },
        },
        repeat_friday: {
            condition: recurrent,
            disabled: false,
            info: "Día(s) en el que se repetirá la sesión",
            type: "checkbox",
            label: "Viernes",
            name: "repeat_friday",
            validators: {
                validate: () =>
                    !recurrent ||
                    repeatSome ||
                    "Elegir al menos un día de la semana es obligatorio",
            },
        },
        repeat_saturday: {
            condition: recurrent,
            disabled: false,
            info: "Día(s) en el que se repetirá la sesión",
            type: "checkbox",
            label: "Sábado",
            name: "repeat_saturday",
            validators: {
                validate: () =>
                    !recurrent ||
                    repeatSome ||
                    "Elegir al menos un día de la semana es obligatorio",
            },
        },
        repeat_sunday: {
            condition: recurrent,
            disabled: false,
            info: "Día(s) en el que se repetirá la sesión",
            type: "checkbox",
            label: "Domingo",
            name: "repeat_sunday",
            validators: {
                validate: () =>
                    !recurrent ||
                    repeatSome ||
                    "Elegir al menos un día de la semana es obligatorio",
            },
        },
    };

    return (
        <form className="mt-3">
            <CustomForm
                formConfig={formConfig}
                errors={errors}
                register={register}
                className="grid grid-cols-1 md:grid-cols-2 gap-3"
            />

            <div className="mt-5 flex space-x-2 sm:space-x-4">
                <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    onClick={handleSubmit(postAbsence, closeTooltip)}
                    disabled={isSubmitting}
                >
                    Confirmar
                </button>
                <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    onClick={closeTooltip}
                    disabled={isSubmitting}
                >
                    Cancelar
                </button>
            </div>
        </form>
    );
};

export default RegisterAbsence;
